import { CustomInput, CustomSwitch, ListDropDown } from 'components';
import { FC } from 'react';
import { dropDownServiceFeeBasePoint } from '../constants';
import { GreyStrokeInfoIcon } from 'assets/icons';
import classnames from 'classnames';
import { twMerge } from 'tailwind-merge';
import { FeatureToggleOption, ServiceFeeBasePoint } from '../type';
import { VoidFn } from 'types/baseTypes';
import ServiceFeeDropDown from './ServiceFeeDropDown';

interface ServiceFeeSectionProps {
  className?: string;
  handleServiceFeeChange: (value: string) => void;
  serviceFeeError: string;
  selectedServiceFeeOption: FeatureToggleOption;
  onServiceFeeOptionClick: (option: FeatureToggleOption) => void;
  surcharge: Record<string, string | boolean>;
  updateServiceFeePercentage: VoidFn;
  selectedBasePoint: ServiceFeeBasePoint;
  onBasePointClick: (option: ServiceFeeBasePoint) => void;
  isServiceFeeEnabled: boolean;
  toggleServiceFee: VoidFn;
}

const ServiceFeeSection: FC<ServiceFeeSectionProps> = ({
  handleServiceFeeChange,
  serviceFeeError,
  selectedServiceFeeOption,
  onServiceFeeOptionClick,
  surcharge,
  updateServiceFeePercentage,
  selectedBasePoint,
  onBasePointClick,
  isServiceFeeEnabled,
  toggleServiceFee
}) => {
  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="text-xl font-bold text-heading">Service fee</div>
          <div className="mt-2 text-17px text-primaryText">
            Service fee helps to streamline invoice creation for maximum efficiency.
          </div>
        </div>
        <div className="ml-6">
          <CustomSwitch id="service-fee-toggle-switch" enabled={isServiceFeeEnabled} toggleSwitch={toggleServiceFee} />
        </div>
      </div>
      {isServiceFeeEnabled && (
        <>
          <div className="mt-5 flex w-full items-center justify-between gap-2">
            <div className="w-3/4 text-17px text-primaryText">
              Customize your Service Fee method to reflect accurately on your invoices
            </div>
            <ServiceFeeDropDown selectedItem={selectedServiceFeeOption} setSelectedItem={onServiceFeeOptionClick} />
          </div>

          <div className="mt-4 mb-3 lg:w-3/5">
            <div className="text-17px text-primaryText">Set your desired service fee</div>
            <div className="mt-3 flex items-center">
              <CustomInput
                id="fee-section-service-fee-percentage"
                value={surcharge.percentage}
                placeholder="0%"
                hasSuffix={true}
                suffix="%"
                onChange={handleServiceFeeChange}
                onBlur={updateServiceFeePercentage}
                suffixStyle="right-4 top-2.5 text-accentText text-17px font-semibold"
                className={twMerge(
                  'w-[150px] h-10 py-2 pl-5 text-sbase font-medium text-primaryText focus:outline-none',
                  classnames({
                    'border border-error': serviceFeeError
                  })
                )}
              />
              <div className="mx-3.5 text-xl font-bold text-accentText">+</div>
              <ListDropDown
                buttonStyles="bg-secondaryBtn h-10 text-sbase font-medium cursor-pointer py-2"
                optionStyles="min-w-[150px]"
                spanStyles="text-headingGray"
                items={dropDownServiceFeeBasePoint}
                selectedItem={selectedBasePoint}
                setSelectedItem={onBasePointClick}
                wrapperStyles="mb-1 bg-primary w-[150px] focus:outline-none"
                id="service-fee-base-point-dropdown"
                itemStyle="hover:bg-highlightGray"
              />
            </div>
          </div>
          {serviceFeeError && <div className="mt-2 text-17px font-semibold text-error">{serviceFeeError}</div>}
          <div className="flex items-center">
            <GreyStrokeInfoIcon height={18} width={18} />
            <div className="ml-1.5 text-sbase text-tertiaryText">
              Service fee is charged to the invoice subtotal including line items and discounts.
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ServiceFeeSection;
